import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, GiftOutlined } from '@ant-design/icons';
import { Button, Card, Col, Divider, message, Row, Space, Statistic, Tag, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useI18n } from '../i18n';
import { apiService } from '../services/api';

const { Title, Text } = Typography;

interface Coupon {
  id: number;
  amount: number;
  status: 'ACTIVE' | 'USED' | 'EXPIRED';
  source: string;
  description: string;
  expiresAt: string;
  usedAt?: string;
  createdAt: string;
}

interface CouponData {
  success: boolean;
  data: {
    activeTotal: number;
    totalAmount: number;
    coupons: Coupon[];
  };
}

const CouponManagement: React.FC = () => {
  const { t, locale } = useI18n();
  const [loading, setLoading] = useState(false);
  const [couponData, setCouponData] = useState<CouponData | null>(null);
  const [expiringCoupons, setExpiringCoupons] = useState<Coupon[]>([]);
  const { isAuthenticated } = useAuth();

  // 获取优惠券数据
  const fetchCouponData = async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    try {
      const [totalResponse, listResponse, expiringResponse] = await Promise.all([
        apiService.getCouponTotal(),
        apiService.getCouponList(),
        apiService.getExpiringCoupons()
      ]);

      if (totalResponse.success) {
        setCouponData({
          success: true,
          data: {
            activeTotal: totalResponse.data.activeTotal,
            totalAmount: totalResponse.data.totalAmount,
            coupons: listResponse.success ? listResponse.data || [] : []
          }
        });
      }

      if (expiringResponse.success) {
        setExpiringCoupons(expiringResponse.data || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch coupon data:', error);
      message.error(t('couponManagement.messages.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCouponData();
  }, [isAuthenticated]);

  // 格式化金额
  const formatAmount = (amount: number) => {
    return amount.toFixed(2);
  };

  // 格式化时间
  const formatDateTime = (dateTime: string) => {
    const dateLocale = locale === 'zh-TW' ? 'zh-TW' : 'en-US';
    return new Date(dateTime).toLocaleString(dateLocale);
  };

  // 计算剩余天数
  const getDaysUntilExpiry = (expiresAt: string) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // 获取状态标签
  const getStatusTag = (coupon: Coupon) => {
    const daysLeft = getDaysUntilExpiry(coupon.expiresAt);

    if (coupon.status === 'USED') {
      return <Tag color="green" icon={<CheckCircleOutlined />}>{t('couponManagement.status.used')}</Tag>;
    } else if (coupon.status === 'EXPIRED') {
      return <Tag color="red" icon={<CloseCircleOutlined />}>{t('couponManagement.status.expired')}</Tag>;
    } else if (daysLeft <= 0) {
      return <Tag color="red" icon={<CloseCircleOutlined />}>{t('couponManagement.status.expired')}</Tag>;
    } else if (daysLeft <= 7) {
      return <Tag color="orange" icon={<ClockCircleOutlined />}>{t('couponManagement.status.expiringSoon')}</Tag>;
    } else {
      return <Tag color="blue" icon={<GiftOutlined />}>{t('couponManagement.status.available')}</Tag>;
    }
  };

  // 获取来源标签
  const getSourceTag = (source: string) => {
    const sourceMap: { [key: string]: { textKey: string; color: string } } = {
      'REFERRAL_FIRST_PURCHASE': { textKey: 'messages.coupon.source.referralFirst', color: 'blue' },
      'REFERRAL_REGULAR_PURCHASE': { textKey: 'messages.coupon.source.referralRegular', color: 'green' },
      'REGISTRATION': { textKey: 'messages.coupon.source.registration', color: 'purple' },
      'MANUAL': { textKey: 'messages.coupon.source.manual', color: 'orange' },
      'ADMIN_GRANT': { textKey: 'messages.coupon.source.adminGrant', color: 'purple' },
      'SYSTEM_REWARD': { textKey: 'messages.coupon.source.systemReward', color: 'orange' }
    };
    const config = sourceMap[source] || { textKey: 'messages.coupon.source.unknown', color: 'default' };
    const text = config.textKey ? t(config.textKey) : source;
    const color = config.color === 'default' ? undefined : config.color;
    return <Tag color={color}>{text}</Tag>;
  };

  if (!isAuthenticated) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Text type="secondary">{t('couponManagement.loginRequired')}</Text>
      </div>
    );
  }

  return (
    <div className="coupon-management" style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <Title level={2} style={{ textAlign: 'center', marginBottom: '32px' }}>
        <GiftOutlined style={{ marginRight: '8px', color: '#52c41a' }} />
        {t('couponManagement.title')}
      </Title>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={8}>
          <Card>
            <Statistic
              title={t('couponManagement.stats.activeTotal')}
              value={couponData?.data?.activeTotal || 0}
              precision={2}
              prefix="NT$"
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title={t('couponManagement.stats.totalAmount')}
              value={couponData?.data?.totalAmount || 0}
              precision={2}
              prefix="NT$"
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title={t('couponManagement.stats.expiringSoon')}
              value={expiringCoupons.length}
              valueStyle={{ color: '#fa8c16' }}
              suffix={t('couponManagement.stats.couponUnit')}
            />
          </Card>
        </Col>
      </Row>

      {/* 优惠券列表 */}
      <Card title={t('couponManagement.list.title')} extra={
        <Button onClick={fetchCouponData} loading={loading}>
          {t('couponManagement.actions.refresh')}
        </Button>
      }>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Text>{t('couponManagement.list.loading')}</Text>
          </div>
        ) : (couponData?.data?.coupons || []).length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Text type="secondary">{t('couponManagement.list.empty')}</Text>
          </div>
        ) : (
          <Row gutter={[16, 16]}>
            {(couponData?.data?.coupons || []).map((coupon) => {
              const daysLeft = getDaysUntilExpiry(coupon.expiresAt);
              const isExpired = coupon.status === 'EXPIRED' || daysLeft <= 0;
              const isExpiringSoon = daysLeft > 0 && daysLeft <= 7;

              return (
                <Col xs={24} sm={12} md={8} lg={6} key={coupon.id}>
                  <Card
                    style={{
                      height: '100%',
                      border: isExpired ? '1px solid #d9d9d9' : '1px solid #52c41a',
                      borderRadius: '8px',
                      position: 'relative',
                      opacity: isExpired ? 0.6 : 1,
                    }}
                    bodyStyle={{ padding: '16px' }}
                  >
                    {/* 优惠券金额 */}
                    <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                      <Text strong style={{
                        fontSize: '24px',
                        color: isExpired ? '#999' : '#52c41a',
                        display: 'block'
                      }}>
                        NT$ {formatAmount(coupon.amount)}
                      </Text>
                    </div>

                    {/* 状态和来源 */}
                    <div style={{ marginBottom: '12px' }}>
                      <Space wrap>
                        {getStatusTag(coupon)}
                        {getSourceTag(coupon.source)}
                      </Space>
                    </div>

                    {/* 描述 */}
                    {coupon.description && (
                      <div style={{ marginBottom: '12px' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {coupon.description}
                        </Text>
                      </div>
                    )}

                    <Divider style={{ margin: '12px 0' }} />

                    {/* 过期时间 */}
                    <div style={{ marginBottom: '8px' }}>
                      <Text style={{ fontSize: '12px', color: '#666' }}>
                        {t('couponManagement.fields.expiresAt')}: {formatDateTime(coupon.expiresAt)}
                      </Text>
                    </div>

                    {/* 剩余天数 */}
                    {!isExpired && (
                      <div style={{ marginBottom: '8px' }}>
                        <Text style={{
                          fontSize: '12px',
                          color: isExpiringSoon ? '#fa8c16' : '#52c41a',
                          fontWeight: 'bold'
                        }}>
                          {t('couponManagement.fields.daysLeft', { days: daysLeft })}
                        </Text>
                      </div>
                    )}

                    {/* 使用时间 */}
                    {coupon.usedAt && (
                      <div>
                        <Text style={{ fontSize: '12px', color: '#666' }}>
                          {t('couponManagement.fields.usedAt')}: {formatDateTime(coupon.usedAt)}
                        </Text>
                      </div>
                    )}

                    {/* 优惠券ID */}
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      fontSize: '10px',
                      color: '#999'
                    }}>
                      {t('couponManagement.fields.id', { id: coupon.id })}
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Card>

      {/* 即将过期提醒 */}
      {expiringCoupons.length > 0 && (
        <Card
          title={
            <span style={{ color: '#fa8c16' }}>
              <ClockCircleOutlined style={{ marginRight: '8px' }} />
              {t('couponManagement.expiring.title')}
            </span>
          }
          style={{ marginTop: '16px' }}
        >
          <div style={{ color: '#fa8c16', marginBottom: '12px' }}>
            {t('couponManagement.expiring.description', { count: expiringCoupons.length })}
          </div>
          <Row gutter={[16, 16]}>
            {expiringCoupons.map((coupon) => {
              const daysLeft = getDaysUntilExpiry(coupon.expiresAt);

              return (
                <Col xs={24} sm={12} md={8} lg={6} key={coupon.id}>
                  <Card
                    style={{
                      height: '100%',
                      border: '1px solid #fa8c16',
                      borderRadius: '8px',
                      position: 'relative',
                    }}
                    bodyStyle={{ padding: '16px' }}
                  >
                    {/* 优惠券金额 */}
                    <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                      <Text strong style={{
                        fontSize: '20px',
                        color: '#fa8c16',
                        display: 'block'
                      }}>
                        NT$ {formatAmount(coupon.amount)}
                      </Text>
                    </div>

                    {/* 状态和来源 */}
                    <div style={{ marginBottom: '12px' }}>
                      <Space wrap>
                        {getStatusTag(coupon)}
                        {getSourceTag(coupon.source)}
                      </Space>
                    </div>

                    <Divider style={{ margin: '12px 0' }} />

                    {/* 过期时间 */}
                    <div style={{ marginBottom: '8px' }}>
                      <Text style={{ fontSize: '12px', color: '#666' }}>
                        {t('couponManagement.fields.expiresAt')}: {formatDateTime(coupon.expiresAt)}
                      </Text>
                    </div>

                    {/* 剩余天数 */}
                    <div style={{ marginBottom: '8px' }}>
                      <Text style={{
                        fontSize: '12px',
                        color: '#fa8c16',
                        fontWeight: 'bold'
                      }}>
                        {t('couponManagement.fields.daysLeft', { days: daysLeft })}
                      </Text>
                    </div>

                    {/* 优惠券ID */}
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      fontSize: '10px',
                      color: '#999'
                    }}>
                      {t('couponManagement.fields.id', { id: coupon.id })}
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Card>
      )}
    </div>
  );
};

export default CouponManagement;
